// src/components/DamperForceGraph.js
import React, { useState, useMemo } from 'react';
import MobileScrollInput from './MobileScrollInput';
import { buildDamperCurves } from '../utils/damperInterpolation';
import '../styles.css';

const SIDES = ['Front', 'Rear'];
const ADJUSTERS = ['LSC', 'HSC', 'LSR', 'HSR'];
const ADJUSTER_LABELS = {
  LSC: 'Low Speed Compression',
  HSC: 'High Speed Compression',
  LSR: 'Low Speed Rebound',
  HSR: 'High Speed Rebound',
};
const ADJUSTER_SHORT = {
  LSC: 'Low Speed Comp.',
  HSC: 'High Speed Comp.',
  LSR: 'Low Speed Reb.',
  HSR: 'High Speed Reb.',
};
const SIDE_COLORS = { Front: '#1E90FF', Rear: '#FF4500' };
const TEST_COLORS = ['#1E90FF', '#FF4500', '#2ecc71', '#9b59b6', '#e67e22', '#1abc9c', '#e91e63', '#795548'];

const defaultSideConfig = () => ({
  enabled: { LSC: false, HSC: false, LSR: false, HSR: false },
  paramMap: {},
  speeds: [],
  datapoints: [],
});

const newDatapointId = () => `dp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

function generatePath(points) {
  if (points.length < 2) return '';
  let pathD = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const c1 = points[i - 1];
    const c2 = points[i];
    const c3 = points[i + 1];
    const c1c2Dist = Math.hypot(c2.x - c1.x, c2.y - c1.y);
    const c2c3Dist = Math.hypot(c3.x - c2.x, c3.y - c2.y);
    const radius = 20;
    const effectiveRadius = Math.min(radius, c1c2Dist / 2, c2c3Dist / 2);
    const c1c2Ratio = (c1c2Dist - effectiveRadius) / c1c2Dist;
    const c1c2Curve = {
      x: (1 - c1c2Ratio) * c1.x + c1c2Ratio * c2.x,
      y: (1 - c1c2Ratio) * c1.y + c1c2Ratio * c2.y,
    };
    const c2c3Ratio = effectiveRadius / c2c3Dist;
    const c2c3Curve = {
      x: (1 - c2c3Ratio) * c2.x + c2c3Ratio * c3.x,
      y: (1 - c2c3Ratio) * c2.y + c2c3Ratio * c3.y,
    };
    pathD += ` L ${c1c2Curve.x},${c1c2Curve.y} Q ${c2.x},${c2.y} ${c2c3Curve.x},${c2c3Curve.y}`;
  }
  pathD += ` L ${points[points.length - 1].x},${points[points.length - 1].y}`;
  return pathD;
}

function curveToPoints(speeds, forces, negate, xScale, yScale) {
  if (!forces) return [];
  return speeds
    .map((s, i) => ({ speed: parseFloat(s), force: negate ? -forces[i] : forces[i] }))
    .filter(p => !isNaN(p.speed) && !isNaN(p.force))
    .sort((a, b) => a.speed - b.speed)
    .map(p => ({ x: xScale(p.speed), y: yScale(p.force) }));
}

function computeExtents(setupObj) {
  let maxSpeed = 0;
  let maxForce = 0;
  SIDES.forEach(side => {
    const cfg = setupObj?.[side];
    if (!cfg) return;
    (cfg.speeds || []).forEach(s => {
      const n = parseFloat(s);
      if (!isNaN(n)) maxSpeed = Math.max(maxSpeed, Math.abs(n));
    });
    (cfg.datapoints || []).forEach(dp => {
      (dp.compressionForces || []).forEach(f => {
        const n = parseFloat(f);
        if (!isNaN(n)) maxForce = Math.max(maxForce, Math.abs(n));
      });
      (dp.reboundForces || []).forEach(f => {
        const n = parseFloat(f);
        if (!isNaN(n)) maxForce = Math.max(maxForce, Math.abs(n));
      });
    });
  });
  return { maxSpeed, maxForce };
}

function flattenParamNames(tabs) {
  const names = [];
  (tabs || []).forEach(tab => (tab.groups || []).forEach(group => (group.params || []).forEach(param => {
    if (param.isDamperGraph) return;
    if (param.leftRight) {
      ['Left', 'Right'].forEach(pos => names.push(`${param.name} (${pos})`));
    } else if (param.isFrontRearGroup) {
      ['Front', 'Rear'].forEach(pos => names.push(`${param.name} (${pos})`));
    } else if (param.isFourCornerGroup) {
      ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].forEach(pos => names.push(`${param.name} (${pos})`));
    } else {
      names.push(param.name);
    }
  })));
  return names;
}

function ParamSearchSelect({ value, options, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = options.filter(name => name.toLowerCase().includes(query.toLowerCase())).slice(0, 25);

  return (
    <div className="param-search">
      <input
        className="param-search-input"
        value={open ? query : (value || '')}
        placeholder="Search parameter..."
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => setQuery(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <div className="param-search-results">
          {filtered.length === 0 && <div className="param-search-result-item">No matches</div>}
          {filtered.map(name => (
            <div
              key={name}
              className="param-search-result-item"
              onClick={() => { onSelect(name); setOpen(false); }}
            >
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DamperForceGraph({ param, params, vehicleTabs, damperSetup, onDamperSetupChange, onParamChange, comparison }) {
  const [showSetup, setShowSetup] = useState(false);
  const [showExpanded, setShowExpanded] = useState(false);
  const [setupSide, setSetupSide] = useState('Front');
  const [graphSide, setGraphSide] = useState('Both');
  const speedUnit = param?.speedUnit || 'mm/s';
  const forceUnit = param?.forceUnit || 'N';

  const paramOptions = useMemo(() => flattenParamNames(vehicleTabs), [vehicleTabs]);

  // Resolve a flattened parameter name (e.g. "Low Speed Compression (Front)")
  // back to its base parameter definition so the live adjuster inputs can
  // reuse the configured step/min/max/unit, matching the setup sheet.
  const getParamDef = useMemo(() => {
    const defs = [];
    (vehicleTabs || []).forEach(tab => (tab.groups || []).forEach(group => (group.params || []).forEach(p => {
      if (!p.isDamperGraph) defs.push(p);
    })));
    return (paramName) => {
      const found = defs.find(p => p.name === paramName || paramName?.startsWith(p.name + ' ('));
      return found || {};
    };
  }, [vehicleTabs]);

  const getSideConfig = (side) => damperSetup?.[side] || defaultSideConfig();

  const updateSide = (side, updater) => {
    onDamperSetupChange({ ...damperSetup, [side]: updater(getSideConfig(side)) });
  };

  const toggleAdjuster = (side, key) => {
    updateSide(side, cfg => {
      const nowEnabled = !cfg.enabled[key];
      const paramMap = { ...cfg.paramMap };
      if (!nowEnabled) delete paramMap[key];
      return { ...cfg, enabled: { ...cfg.enabled, [key]: nowEnabled }, paramMap };
    });
  };

  const setParamMap = (side, key, name) => {
    updateSide(side, cfg => ({ ...cfg, paramMap: { ...cfg.paramMap, [key]: name } }));
  };

  const addSpeed = (side) => {
    updateSide(side, cfg => {
      const nums = cfg.speeds.map(s => parseFloat(s)).filter(n => !isNaN(n));
      const nextSpeed = nums.length ? Math.max(...nums) + 50 : 50;
      return {
        ...cfg,
        speeds: [...cfg.speeds, nextSpeed],
        datapoints: cfg.datapoints.map(dp => ({
          ...dp,
          compressionForces: dp.compressionForces ? [...dp.compressionForces, ''] : dp.compressionForces,
          reboundForces: dp.reboundForces ? [...dp.reboundForces, ''] : dp.reboundForces,
        })),
      };
    });
  };

  const removeSpeed = (side, idx) => {
    updateSide(side, cfg => ({
      ...cfg,
      speeds: cfg.speeds.filter((_, i) => i !== idx),
      datapoints: cfg.datapoints.map(dp => ({
        ...dp,
        compressionForces: dp.compressionForces ? dp.compressionForces.filter((_, i) => i !== idx) : dp.compressionForces,
        reboundForces: dp.reboundForces ? dp.reboundForces.filter((_, i) => i !== idx) : dp.reboundForces,
      })),
    }));
  };

  const updateSpeedValue = (side, idx, value) => {
    updateSide(side, cfg => ({ ...cfg, speeds: cfg.speeds.map((s, i) => (i === idx ? value : s)) }));
  };

  // Display order of speed columns, slowest → fastest. Storage order is left
  // untouched (so editing never loses focus); the speeds list and the force
  // tables both render through this order, keyed by the original index so the
  // sort updates live as values are typed.
  const speedDisplayOrder = (cfg) => cfg.speeds
    .map((s, i) => ({ n: parseFloat(s), i }))
    .sort((a, b) => (isNaN(a.n) ? Infinity : a.n) - (isNaN(b.n) ? Infinity : b.n))
    .map(x => x.i);

  const addDatapoint = (side) => {
    updateSide(side, cfg => ({
      ...cfg,
      datapoints: [...cfg.datapoints, {
        id: newDatapointId(),
        LSC: '', HSC: '', LSR: '', HSR: '',
        compressionForces: cfg.speeds.map(() => ''),
        reboundForces: cfg.speeds.map(() => ''),
      }],
    }));
  };

  const deleteDatapoint = (side, idx) => {
    updateSide(side, cfg => ({ ...cfg, datapoints: cfg.datapoints.filter((_, i) => i !== idx) }));
  };

  const updateDatapointField = (side, idx, field, value) => {
    updateSide(side, cfg => ({
      ...cfg,
      datapoints: cfg.datapoints.map((dp, i) => (i === idx ? { ...dp, [field]: value } : dp)),
    }));
  };

  const updateForce = (side, idx, forcesField, speedIdx, value) => {
    updateSide(side, cfg => ({
      ...cfg,
      datapoints: cfg.datapoints.map((dp, i) => (
        i === idx ? { ...dp, [forcesField]: dp[forcesField].map((f, j) => (j === speedIdx ? value : f)) } : dp
      )),
    }));
  };

  const sideCurves = useMemo(() => {
    const result = {};
    SIDES.forEach(side => {
      const cfg = getSideConfig(side);
      const liveValues = {};
      ADJUSTERS.forEach(key => {
        if (cfg.enabled[key] && cfg.paramMap[key]) {
          liveValues[key] = params?.[cfg.paramMap[key]];
        }
      });
      result[side] = buildDamperCurves(cfg, liveValues);
    });
    return result;
  }, [damperSetup, params]); // eslint-disable-line react-hooks/exhaustive-deps

  // Softest (min adjuster) and stiffest (max adjuster) envelope curves, so the
  // graph shows the full adjustment band the live curve sits between.
  const envelopeCurves = useMemo(() => {
    const result = {};
    SIDES.forEach(side => {
      const cfg = getSideConfig(side);
      const minValues = {};
      const maxValues = {};
      ADJUSTERS.forEach(key => {
        if (!cfg.enabled[key]) return;
        const vals = cfg.datapoints
          .map(dp => parseFloat(dp[key]))
          .filter(n => !isNaN(n));
        if (vals.length === 0) return;
        minValues[key] = Math.min(...vals);
        maxValues[key] = Math.max(...vals);
      });
      result[side] = {
        min: buildDamperCurves(cfg, minValues),
        max: buildDamperCurves(cfg, maxValues),
      };
    });
    return result;
  }, [damperSetup]); // eslint-disable-line react-hooks/exhaustive-deps

  const comparisonCurves = useMemo(() => {
    const result = {};
    if (!comparison?.damperSetup) return result;
    SIDES.forEach(side => {
      const cfg = comparison.damperSetup[side];
      if (!cfg) {
        result[side] = { speeds: [], compression: null, rebound: null };
        return;
      }
      const liveValues = {};
      ADJUSTERS.forEach(key => {
        if (cfg.enabled?.[key] && cfg.paramMap?.[key]) {
          liveValues[key] = comparison.setup?.[cfg.paramMap[key]];
        }
      });
      result[side] = buildDamperCurves(cfg, liveValues);
    });
    return result;
  }, [comparison]);

  // SVG constants (mirrors EnginePowerGraph)
  const SVG_WIDTH = 600;
  const SVG_HEIGHT = 500;

  // Y-axis is fixed to the full range of measured dyno data (the stiffest
  // test), not the currently-interpolated value, so the curve's shape change
  // is visible as settings change rather than the axis rescaling every time.
  const { maxSpeed: rawMaxSpeed, maxForce: rawMaxForce } = useMemo(() => {
    const own = computeExtents(damperSetup);
    const comp = comparison?.damperSetup ? computeExtents(comparison.damperSetup) : { maxSpeed: 0, maxForce: 0 };
    return { maxSpeed: Math.max(own.maxSpeed, comp.maxSpeed), maxForce: Math.max(own.maxForce, comp.maxForce) };
  }, [damperSetup, comparison]);

  // When the user has entered speeds, end the axis exactly at the largest one
  // so the (entered-speed) gridlines line up with the plot edge.
  const maxSpeed = rawMaxSpeed > 0 ? rawMaxSpeed : 100;
  const maxForce = Math.ceil((rawMaxForce || 100) / 50) * 50;

  // X-axis ticks come from the actual dyno speeds the user entered (union of
  // both sides), so the grid lines up with the data points. Falls back to an
  // even split when no speeds have been entered yet.
  const enteredSpeeds = useMemo(() => {
    const set = new Set([0]);
    SIDES.forEach(side => {
      (damperSetup?.[side]?.speeds || []).forEach(s => {
        const n = parseFloat(s);
        if (!isNaN(n) && n > 0) set.add(n);
      });
    });
    const arr = Array.from(set).sort((a, b) => a - b);
    return arr.length > 1 ? arr : null;
  }, [damperSetup]);

  const xTicks = enteredSpeeds || (() => {
    const t = [];
    for (let s = 0; s <= maxSpeed; s += Math.max(50, Math.round(maxSpeed / 6 / 10) * 10)) t.push(s);
    return t;
  })();
  const yTicks = [];
  for (let f = -maxForce; f <= maxForce; f += Math.max(50, Math.round(maxForce / 4 / 10) * 10)) yTicks.push(f);

  const visibleSides = graphSide === 'Both' ? SIDES : [graphSide];

  const legendEntries = [];
  visibleSides.forEach(side => {
    const { compression, rebound } = sideCurves[side];
    if (compression) legendEntries.push({ key: `${side}-c`, label: `${side} Compression`, color: SIDE_COLORS[side], dashed: false });
    if (rebound) legendEntries.push({ key: `${side}-r`, label: `${side} Rebound`, color: SIDE_COLORS[side], dashed: true });
  });
  if (comparison?.damperSetup) {
    visibleSides.forEach(side => {
      const curves = comparisonCurves[side];
      if (!curves) return;
      if (curves.compression) legendEntries.push({ key: `cmp-${side}-c`, label: `${side} Compression (comparison)`, color: SIDE_COLORS[side], dashed: true, faded: true });
      if (curves.rebound) legendEntries.push({ key: `cmp-${side}-r`, label: `${side} Rebound (comparison)`, color: SIDE_COLORS[side], dashed: true, faded: true });
    });
  }

  const renderSideToggle = () => (
    <div className="damper-side-toggle">
      {['Both', ...SIDES].map(opt => (
        <button
          key={opt}
          type="button"
          className={`damper-side-toggle-btn ${graphSide === opt ? 'active' : ''}`}
          onClick={() => setGraphSide(opt)}
        >
          {opt !== 'Both' && <span className="damper-side-dot" style={{ backgroundColor: SIDE_COLORS[opt] }} />}
          {opt}
        </button>
      ))}
    </div>
  );

  const renderGraph = (simple = false) => {
    // Simple mode (compact card) drops axis labels, so it needs only a thin
    // margin — this removes the empty gutter on the left where labels would be.
    const margin = simple
      ? { top: 12, right: 12, bottom: 12, left: 12 }
      : { top: 30, right: 20, bottom: 50, left: 82 };
    const graphWidth = SVG_WIDTH - margin.left - margin.right;
    const graphHeight = SVG_HEIGHT - margin.top - margin.bottom;
    const zeroY = graphHeight / 2;
    const xScale = (speed) => (speed / maxSpeed) * graphWidth;
    const yScale = (force) => zeroY - (force / maxForce) * zeroY;

    return (
      <g transform={`translate(${margin.left},${margin.top})`}>
        <line x1="0" y1={zeroY} x2={graphWidth} y2={zeroY} stroke="#333" strokeWidth="2" />
        <line x1="0" y1="0" x2="0" y2={graphHeight} stroke="#333" strokeWidth="2" />
        {!simple && (
          <>
            <text x={graphWidth / 2} y={graphHeight + 40} textAnchor="middle" className="graph-label">Speed ({speedUnit})</text>
            <text x={-graphHeight / 2} y={-62} textAnchor="middle" transform="rotate(-90)" className="graph-label">Force ({forceUnit})</text>
          </>
        )}
        <text x={6} y={20} className="damper-region-label">Compression</text>
        <text x={6} y={graphHeight - 6} className="damper-region-label">Rebound</text>
        {xTicks.map((s, i) => (
          <line key={`x-grid-${i}`} x1={xScale(s)} y1="0" x2={xScale(s)} y2={graphHeight} stroke="#ccc" strokeDasharray="2,2" />
        ))}
        {yTicks.map((f, i) => (
          <line key={`y-grid-${i}`} x1="0" y1={yScale(f)} x2={graphWidth} y2={yScale(f)} stroke="#ccc" strokeDasharray="2,2" />
        ))}
        {comparison?.damperSetup && visibleSides.map(side => {
          const curves = comparisonCurves[side];
          if (!curves) return null;
          const { speeds, compression, rebound } = curves;
          const compressionPath = generatePath(curveToPoints(speeds, compression, false, xScale, yScale));
          const reboundPath = generatePath(curveToPoints(speeds, rebound, true, xScale, yScale));
          return (
            <React.Fragment key={`comparison-${side}`}>
              {compressionPath && <path d={compressionPath} fill="none" stroke={SIDE_COLORS[side]} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="4,3" strokeOpacity="0.4" />}
              {reboundPath && <path d={reboundPath} fill="none" stroke={SIDE_COLORS[side]} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="2,3" strokeOpacity="0.4" />}
            </React.Fragment>
          );
        })}
        {visibleSides.map(side => {
          const env = envelopeCurves[side];
          if (!env) return null;
          const bounds = [];
          ['min', 'max'].forEach(bound => {
            const { speeds, compression, rebound } = env[bound];
            const cPath = generatePath(curveToPoints(speeds, compression, false, xScale, yScale));
            const rPath = generatePath(curveToPoints(speeds, rebound, true, xScale, yScale));
            if (cPath) bounds.push(<path key={`env-${side}-${bound}-c`} d={cPath} fill="none" stroke={SIDE_COLORS[side]} strokeWidth="1.5" strokeDasharray="3,4" strokeOpacity="0.3" />);
            if (rPath) bounds.push(<path key={`env-${side}-${bound}-r`} d={rPath} fill="none" stroke={SIDE_COLORS[side]} strokeWidth="1.5" strokeDasharray="3,4" strokeOpacity="0.3" />);
          });
          return <React.Fragment key={`env-${side}`}>{bounds}</React.Fragment>;
        })}
        {visibleSides.map(side => {
          const { speeds, compression, rebound } = sideCurves[side];
          const compressionPath = generatePath(curveToPoints(speeds, compression, false, xScale, yScale));
          const reboundPath = generatePath(curveToPoints(speeds, rebound, true, xScale, yScale));
          return (
            <React.Fragment key={side}>
              {compressionPath && <path d={compressionPath} fill="none" stroke={SIDE_COLORS[side]} strokeWidth="3" strokeLinecap="round" />}
              {reboundPath && <path d={reboundPath} fill="none" stroke={SIDE_COLORS[side]} strokeWidth="3" strokeLinecap="round" strokeDasharray="6,4" />}
            </React.Fragment>
          );
        })}
        {!simple && xTicks.map((s, i) => (
          <text key={`x-tick-${i}`} x={xScale(s)} y={graphHeight + 20} textAnchor="middle" className="tick-label">{s}</text>
        ))}
        {!simple && yTicks.map((f, i) => (
          <text key={`y-tick-${i}`} x={-10} y={yScale(f) + 5} textAnchor="end" className="tick-label">{f}</text>
        ))}
      </g>
    );
  };

  const renderForceTable = (side, cfg, direction) => {
    const keys = direction === 'compression' ? ['LSC', 'HSC'] : ['LSR', 'HSR'];
    const forcesField = direction === 'compression' ? 'compressionForces' : 'reboundForces';
    const activeKeys = keys.filter(key => cfg.enabled[key]);
    if (activeKeys.length === 0) return null;
    const accent = direction === 'compression' ? 'damper-table-compression' : 'damper-table-rebound';
    const order = speedDisplayOrder(cfg);
    return (
      <div className={`damper-datapoint-table ${accent}`}>
        <h4 className="damper-table-title">
          <span className="damper-table-dot" />
          {direction === 'compression' ? 'Compression' : 'Rebound'}
        </h4>
        <div className="damper-table-scroll">
          <table className="damper-force-table">
            <thead>
              <tr className="damper-group-head">
                <th className="damper-col-index"></th>
                <th className="damper-group-settings" colSpan={activeKeys.length}>Adjuster Clicks</th>
                <th className="damper-group-forces" colSpan={cfg.speeds.length || 1}>Force ({forceUnit}) at Speed ({speedUnit})</th>
                <th className="damper-col-action"></th>
              </tr>
              <tr className="damper-sub-head">
                <th className="damper-col-index">Test</th>
                {activeKeys.map(key => <th key={key} className="damper-cell-settings">{key}</th>)}
                {order.map((origIdx, i) => (
                  <th key={`speed-${origIdx}`} className={`damper-cell-forces ${i === 0 ? 'damper-group-divider' : ''}`}>{cfg.speeds[origIdx] || '–'}</th>
                ))}
                <th className="damper-col-action"></th>
              </tr>
            </thead>
            <tbody>
              {cfg.datapoints.map((dp, dpIdx) => (
                <tr key={dp.id}>
                  <td className="damper-col-index">{dpIdx + 1}</td>
                  {activeKeys.map(key => (
                    <td key={key} className="damper-cell-settings">
                      <input
                        type="number"
                        className="damper-compact-input"
                        value={dp[key]}
                        onChange={(e) => updateDatapointField(side, dpIdx, key, e.target.value)}
                      />
                    </td>
                  ))}
                  {order.map((origIdx, i) => (
                    <td key={`f-${origIdx}`} className={`damper-cell-forces ${i === 0 ? 'damper-group-divider' : ''}`}>
                      <input
                        type="number"
                        className="damper-compact-input"
                        value={dp[forcesField]?.[origIdx] ?? ''}
                        onChange={(e) => updateForce(side, dpIdx, forcesField, origIdx, e.target.value)}
                      />
                    </td>
                  ))}
                  <td className="damper-col-action">
                    <i className="material-icons damper-delete-icon" onClick={() => deleteDatapoint(side, dpIdx)} title="Delete test">delete</i>
                  </td>
                </tr>
              ))}
              {cfg.datapoints.length === 0 && (
                <tr>
                  <td className="damper-table-empty" colSpan={activeKeys.length + (cfg.speeds.length || 1) + 2}>No tests yet — add one below.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTestsPreview = (cfg) => {
    const PREVIEW_WIDTH = 400;
    const PREVIEW_HEIGHT = 300;
    const previewMargin = { top: 14, right: 16, bottom: 44, left: 60 };
    const graphWidth = PREVIEW_WIDTH - previewMargin.left - previewMargin.right;
    const graphHeight = PREVIEW_HEIGHT - previewMargin.top - previewMargin.bottom;
    const previewZeroY = graphHeight / 2;

    const speeds = (cfg.speeds || []).map(s => parseFloat(s)).filter(s => !isNaN(s));
    let maxForce = 100;
    cfg.datapoints.forEach(dp => {
      (dp.compressionForces || []).forEach(f => { const n = parseFloat(f); if (!isNaN(n)) maxForce = Math.max(maxForce, Math.abs(n)); });
      (dp.reboundForces || []).forEach(f => { const n = parseFloat(f); if (!isNaN(n)) maxForce = Math.max(maxForce, Math.abs(n)); });
    });
    maxForce = Math.ceil(maxForce / 50) * 50;
    // End the X axis exactly at the largest entered speed so gridlines align.
    const maxSpeed = speeds.length ? Math.max(...speeds) : 100;

    const previewXScale = (speed) => (speed / maxSpeed) * graphWidth;
    const previewYScale = (force) => previewZeroY - (force / maxForce) * previewZeroY;

    // X ticks from the entered speeds (slowest → fastest, plus 0).
    const sideSpeedTicks = (() => {
      const set = new Set([0]);
      speeds.forEach(n => { if (n > 0) set.add(n); });
      const arr = Array.from(set).sort((a, b) => a - b);
      return arr.length > 1 ? arr : null;
    })();
    const xTicks = sideSpeedTicks || (() => {
      const t = [];
      for (let s = 0; s <= maxSpeed; s += Math.max(50, Math.round(maxSpeed / 5 / 10) * 10)) t.push(s);
      return t;
    })();
    const yStep = Math.max(50, Math.round(maxForce / 4 / 10) * 10);
    const yTicks = [];
    for (let f = -maxForce; f <= maxForce; f += yStep) yTicks.push(f);

    const hasData = cfg.datapoints.length > 0;

    return (
      <div className="damper-tests-preview">
        <svg viewBox={`0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}`} className="damper-preview-graph">
          <g transform={`translate(${previewMargin.left},${previewMargin.top})`}>
            {xTicks.map((s, i) => (
              <line key={`px-grid-${i}`} x1={previewXScale(s)} y1="0" x2={previewXScale(s)} y2={graphHeight} stroke="#e2e8f0" strokeDasharray="2,2" />
            ))}
            {yTicks.map((f, i) => (
              <line key={`py-grid-${i}`} x1="0" y1={previewYScale(f)} x2={graphWidth} y2={previewYScale(f)} stroke="#e2e8f0" strokeDasharray="2,2" />
            ))}
            <line x1="0" y1={previewZeroY} x2={graphWidth} y2={previewZeroY} stroke="#333" strokeWidth="1.5" />
            <line x1="0" y1="0" x2="0" y2={graphHeight} stroke="#333" strokeWidth="1.5" />
            {cfg.datapoints.map((dp, dpIdx) => {
              const color = TEST_COLORS[dpIdx % TEST_COLORS.length];
              const compressionPath = generatePath(curveToPoints(cfg.speeds, dp.compressionForces, false, previewXScale, previewYScale));
              const reboundPath = generatePath(curveToPoints(cfg.speeds, dp.reboundForces, true, previewXScale, previewYScale));
              return (
                <React.Fragment key={dp.id}>
                  {compressionPath && <path d={compressionPath} fill="none" stroke={color} strokeWidth="2" />}
                  {reboundPath && <path d={reboundPath} fill="none" stroke={color} strokeWidth="2" strokeDasharray="5,3" />}
                </React.Fragment>
              );
            })}
            {!hasData && (
              <text x={graphWidth / 2} y={previewZeroY - 8} textAnchor="middle" className="damper-preview-placeholder">No tests entered yet</text>
            )}
            <text x={5} y={12} className="damper-region-label">Compression</text>
            <text x={5} y={graphHeight - 5} className="damper-region-label">Rebound</text>
            {xTicks.map((s, i) => (
              <text key={`px-tick-${i}`} x={previewXScale(s)} y={graphHeight + 16} textAnchor="middle" className="tick-label">{s}</text>
            ))}
            {yTicks.map((f, i) => (
              <text key={`py-tick-${i}`} x={-8} y={previewYScale(f) + 4} textAnchor="end" className="tick-label">{f}</text>
            ))}
            <text x={graphWidth / 2} y={graphHeight + 36} textAnchor="middle" className="tick-label">Speed ({speedUnit})</text>
            <text x={-graphHeight / 2} y={-48} textAnchor="middle" transform="rotate(-90)" className="tick-label">Force ({forceUnit})</text>
          </g>
        </svg>
        {hasData && (
          <div className="damper-legend damper-legend-compact">
            {cfg.datapoints.map((dp, dpIdx) => (
              <span key={dp.id} className="damper-legend-item">
                <span className="damper-legend-swatch" style={{ backgroundColor: TEST_COLORS[dpIdx % TEST_COLORS.length] }} />
                Test {dpIdx + 1}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSideSetup = (side) => {
    const cfg = getSideConfig(side);
    const enabledKeys = ADJUSTERS.filter(key => cfg.enabled[key]);
    return (
      <div key={side} className="damper-side-section">
        <div className="damper-setup-top-row">
          <div className="damper-setup-config">
            <div className="damper-config-block">
              <span className="damper-config-label">Available Adjusters</span>
              <div className="damper-toggle-grid">
                {ADJUSTERS.map(key => (
                  <button
                    key={key}
                    type="button"
                    className={`damper-toggle-pill ${cfg.enabled[key] ? 'active' : ''}`}
                    onClick={() => toggleAdjuster(side, key)}
                  >
                    <i className="material-icons">{cfg.enabled[key] ? 'check_circle' : 'radio_button_unchecked'}</i>
                    {ADJUSTER_SHORT[key]}
                  </button>
                ))}
              </div>
            </div>
            {enabledKeys.length > 0 && (
              <div className="damper-config-block">
                <span className="damper-config-label">Linked Setup Parameters</span>
                <div className="damper-param-map">
                  {enabledKeys.map(key => (
                    <div key={key} className="damper-param-map-row">
                      <span className="damper-param-map-label">{ADJUSTER_SHORT[key]}</span>
                      <ParamSearchSelect
                        value={cfg.paramMap[key]}
                        options={paramOptions}
                        onSelect={(name) => setParamMap(side, key, name)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="damper-setup-preview-wrap">
            {renderTestsPreview(cfg)}
          </div>
        </div>

        <div className="damper-config-block damper-speeds-block">
          <span className="damper-config-label">Dyno Speeds ({speedUnit})</span>
          <div className="damper-speed-row">
            {speedDisplayOrder(cfg).map((origIdx) => (
              <span key={origIdx} className="damper-speed-chip">
                <input
                  type="number"
                  className="damper-compact-input"
                  value={cfg.speeds[origIdx]}
                  onChange={(e) => updateSpeedValue(side, origIdx, e.target.value)}
                />
                <i className="material-icons" onClick={() => removeSpeed(side, origIdx)} title="Remove speed">close</i>
              </span>
            ))}
            <button onClick={() => addSpeed(side)} className="btn btn-group-action add" title="Add speed">+</button>
          </div>
        </div>

        {enabledKeys.length === 0 ? (
          <p className="damper-empty-state">Select at least one adjuster above to enter dyno test data.</p>
        ) : (
          <>
            {renderForceTable(side, cfg, 'compression')}
            {renderForceTable(side, cfg, 'rebound')}
            <button onClick={() => addDatapoint(side)} className="damper-add-test-btn">
              <i className="material-icons">add</i> Add Test
            </button>
          </>
        )}
      </div>
    );
  };

  // Live damper click-setting adjusters shown in the expanded view: editable
  // inputs bound to the mapped session parameters so the user can dial in
  // settings and watch the force curve update without leaving the modal.
  const renderLiveAdjusters = () => {
    const sections = SIDES.map(side => {
      const cfg = getSideConfig(side);
      const rows = ADJUSTERS.filter(key => cfg.enabled[key] && cfg.paramMap[key]);
      if (rows.length === 0) return null;
      return (
        <div key={side} className="damper-adjuster-card">
          <h4 className="damper-adjuster-card-title">
            <span className="damper-side-dot" style={{ backgroundColor: SIDE_COLORS[side] }} />
            {side}
          </h4>
          {rows.map(key => {
            const paramName = cfg.paramMap[key];
            const def = getParamDef(paramName);
            return (
              <div key={key} className="damper-adjuster-control">
                <span className="damper-adjuster-control-label">{ADJUSTER_LABELS[key]}</span>
                <MobileScrollInput
                  value={params?.[paramName] ?? ''}
                  onChange={(val) => onParamChange && onParamChange(paramName, val)}
                  step={def.step || '1'}
                  min={def.min}
                  max={def.max}
                  unit={def.unit || ''}
                  id={`damper-live-${side}-${key}`}
                />
              </div>
            );
          })}
        </div>
      );
    }).filter(Boolean);

    if (sections.length === 0) {
      return <p className="damper-empty-state">No adjusters configured — use the gear button to set up the damper.</p>;
    }
    return <div className="damper-adjuster-row">{sections}</div>;
  };

  return (
    <>
      <div className="tab-group damper-graph-card">
        <div className="tab-group-title-container">
          <h3 className="tab-group-title">Damper Force Graph</h3>
          <div className="graph-action-group">
            <button onClick={() => setShowSetup(true)} className="graph-action-btn" title="Damper Setup">
              <i className="material-icons">settings</i>
            </button>
            <button onClick={() => setShowExpanded(true)} className="graph-action-btn" title="Expand">
              <i className="material-icons">open_in_full</i>
            </button>
          </div>
        </div>
        {renderSideToggle()}
        <div className="damper-graph-container">
          <svg viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} preserveAspectRatio="xMidYMid meet" className="damper-graph">
            {renderGraph(true)}
          </svg>
        </div>
      </div>
      {showSetup && (
        <div className="overlay-card">
          <div className="card-container damper-setup-fullscreen">
            <div className="page-title">
              <h2>Damper Setup</h2>
              <button onClick={() => setShowSetup(false)} className="close-button">✕</button>
            </div>
            <div className="damper-side-tabs">
              {SIDES.map(side => (
                <button
                  key={side}
                  type="button"
                  className={`damper-side-tab ${setupSide === side ? 'active' : ''}`}
                  onClick={() => setSetupSide(side)}
                >
                  <span className="damper-side-dot" style={{ backgroundColor: SIDE_COLORS[side] }} />
                  {side}
                </button>
              ))}
            </div>
            {renderSideSetup(setupSide)}
            <div className="event-editor-actions">
              <button onClick={() => setShowSetup(false)} className="btn btn-save">Done</button>
            </div>
          </div>
        </div>
      )}
      {showExpanded && (
        <div className="overlay-card">
          <div className="card-container damper-setup-fullscreen">
            <div className="page-title">
              <h2>Damper Force Graph</h2>
              <button onClick={() => setShowExpanded(false)} className="close-button">✕</button>
            </div>
            <div className="damper-expanded-layout">
              <div className="damper-expanded-adjusters">
                <h3 className="damper-section-heading">Adjustments</h3>
                {renderLiveAdjusters()}
              </div>
              <div className="damper-expanded-graph">
                {renderSideToggle()}
                <div className="damper-graph-container">
                  <svg viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} preserveAspectRatio="xMidYMid meet" className="damper-graph damper-graph-expanded">
                    {renderGraph(false)}
                  </svg>
                </div>
                {legendEntries.length > 0 && (
                  <div className="damper-legend">
                    {legendEntries.map(entry => (
                      <span key={entry.key} className={`damper-legend-item ${entry.faded ? 'damper-legend-item-faded' : ''}`}>
                        <span className="damper-legend-swatch" style={{ borderTopColor: entry.color, borderTopStyle: entry.dashed ? 'dashed' : 'solid', opacity: entry.faded ? 0.5 : 1 }} />
                        {entry.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="event-editor-actions">
              <button onClick={() => setShowExpanded(false)} className="btn btn-save">Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DamperForceGraph;
